import { useEffect, useRef, useState } from 'react'
import type { Column, TableControls } from '../lib/useTableControls'

// The visible half of useTableControls: one search box above a table, and a
// header cell per column that sorts on click and filters from a small popover.
//
// The filter offers the values actually present in the loaded rows, not a
// hard-coded list, so a status the server starts returning tomorrow appears
// without anyone editing this file — and a value that never occurs is never
// offered, which would otherwise read as "no results" for a filter that could
// not match anything.

function SortMark({ dir }: { dir: 'asc' | 'desc' | null }): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={`ml-1 text-[9px] leading-none ${dir ? 'text-accent' : 'text-slate-600'}`}
    >
      {dir === 'asc' ? '▲' : dir === 'desc' ? '▼' : '⇅'}
    </span>
  )
}

export function TableHead<T>({ t }: { t: TableControls<T> }): JSX.Element {
  return (
    <thead>
      <tr>
        {t.columns.map((c) => (
          <Th key={c.key} col={c} t={t} />
        ))}
      </tr>
    </thead>
  )
}

function Th<T>({ col, t }: { col: Column<T>; t: TableControls<T> }): JSX.Element {
  const [open, setOpen] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)
  const kind = col.filter ?? (col.value ? 'select' : 'none')
  const sortable = col.sortable !== false && !!col.value
  const dir = t.sort?.key === col.key ? t.sort.dir : null
  const chosen = t.filters[col.key] ?? []
  const text = t.textFilters[col.key] ?? ''
  const active = chosen.length > 0 || text !== ''

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent): void => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false)
    }
    const onEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  const values = open && kind === 'select' ? t.distinct(col.key) : []

  return (
    <th className={col.className}>
      <div className="flex items-center gap-1" ref={wrap} style={{ position: 'relative' }}>
        {sortable ? (
          <button
            type="button"
            onClick={() => t.toggleSort(col.key)}
            data-testid={`sort-${col.key}`}
            aria-label={`Sort by ${col.label}`}
            className="inline-flex items-center hover:text-white"
            style={{ font: 'inherit', color: 'inherit', background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
          >
            {col.label}
            <SortMark dir={dir} />
          </button>
        ) : (
          <span>{col.label}</span>
        )}

        {kind !== 'none' && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid={`filter-${col.key}`}
            aria-label={`Filter by ${col.label}`}
            aria-expanded={open}
            className={`text-[10px] leading-none px-1 rounded ${
              active ? 'text-accent bg-accent/15' : 'text-slate-600 hover:text-slate-300'
            }`}
            style={{ border: 0, cursor: 'pointer' }}
          >
            ▾
          </button>
        )}

        {open && (
          <div
            data-testid={`filter-panel-${col.key}`}
            className="absolute z-30 mt-1 rounded-lg p-2 text-left"
            style={{
              top: '100%',
              left: 0,
              minWidth: 190,
              maxHeight: 290,
              overflowY: 'auto',
              background: 'rgba(13,20,36,0.98)',
              border: '1px solid rgba(255,255,255,0.12)',
              boxShadow: '0 12px 32px rgba(0,0,0,0.5)'
            }}
          >
            {kind === 'text' ? (
              <input
                autoFocus
                value={text}
                onChange={(e) => t.setTextFilter(col.key, e.target.value)}
                placeholder={`Contains…`}
                data-testid={`filter-text-${col.key}`}
                className="w-full rounded-md bg-white/[0.04] border border-white/10 px-2 py-1 text-xs text-slate-100 focus:outline-none focus:border-accent/50"
                style={{ textTransform: 'none', letterSpacing: 0 }}
              />
            ) : values.length === 0 ? (
              <div className="text-[11px] text-slate-500 px-1 py-0.5" style={{ textTransform: 'none' }}>
                Nothing to filter by yet.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-1 px-1">
                  <span className="text-[10px] text-slate-500">{values.length} values</span>
                  {chosen.length > 0 && (
                    <button
                      type="button"
                      onClick={() => t.setFilter(col.key, [])}
                      className="text-[10px] text-accent hover:underline"
                      style={{ border: 0, background: 'none', cursor: 'pointer' }}
                    >
                      clear
                    </button>
                  )}
                </div>
                {values.map((v) => {
                  const on = chosen.includes(v)
                  return (
                    <label
                      key={v}
                      className="flex items-center gap-2 px-1 py-0.5 rounded hover:bg-white/5 cursor-pointer"
                      style={{ textTransform: 'none', letterSpacing: 0 }}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() =>
                          t.setFilter(col.key, on ? chosen.filter((x) => x !== v) : [...chosen, v])
                        }
                      />
                      <span className="text-[11px] text-slate-200 truncate" title={v}>
                        {v}
                      </span>
                    </label>
                  )
                })}
              </>
            )}
          </div>
        )}
      </div>
    </th>
  )
}

/** The search box and the "showing N of M" line that belongs with every table. */
export function TableToolbar<T>({
  t,
  placeholder,
  noun = 'rows',
  children
}: {
  t: TableControls<T>
  placeholder?: string
  noun?: string
  children?: React.ReactNode
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <input
        value={t.search}
        onChange={(e) => t.setSearch(e.target.value)}
        placeholder={placeholder ?? `Search ${noun}…`}
        data-testid="table-search"
        className="flex-1 min-w-[15rem] rounded-md bg-white/[0.03] border border-white/10 px-3 py-1.5 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-accent/50"
      />
      {children}
      {t.activeCount > 0 && (
        <button
          type="button"
          onClick={t.clearAll}
          data-testid="table-clear"
          className="px-2.5 py-1.5 rounded-md text-xs border border-white/10 text-slate-300 hover:border-white/25"
        >
          Clear {t.activeCount} filter{t.activeCount === 1 ? '' : 's'}
        </button>
      )}
      <span className="text-[11px] text-slate-500 tabular-nums" data-testid="table-count">
        {t.rows.length === t.total
          ? `${t.total} ${noun}`
          : `${t.rows.length} of ${t.total} ${noun}`}
      </span>
    </div>
  )
}

/** Shown in place of rows when a filter excluded everything. */
export function NoMatches<T>({ t, noun = 'rows' }: { t: TableControls<T>; noun?: string }): JSX.Element {
  return (
    <div className="glass-card rounded-xl p-6 text-sm text-slate-400" data-testid="table-no-matches">
      No {noun} match the search or filters.{' '}
      <button
        type="button"
        onClick={t.clearAll}
        className="text-accent hover:underline"
        style={{ border: 0, background: 'none', cursor: 'pointer' }}
      >
        Clear them
      </button>{' '}
      to see all {t.total}.
    </div>
  )
}
